import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  BehaviorSubject,
  map,
  Observable,
  scan,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { UserAccountsService } from '../../services/user-accounts/user-accounts.service';
import { IUserAccount } from '../../services/user-accounts/model/user-account.interface';
import { UserInformationService } from '../../../core/services/user/user-information.service';
import { IUserAccounts } from '../../services/user-accounts/model/user-accounts.interface';
import { shareReplay } from 'rxjs/operators';
import {
  InfiniteScrollComponent,
  OptionListComponent,
} from '@ui-lib/components';
import { AsyncPipe } from '@angular/common';
import { UserAccountItemComponent } from './user-account-item/user-account-item.component';
import { TranslateModule } from '@ngx-translate/core';
import { UserAccountItemSkeletonComponent } from './user-account-item-skeleton/user-account-item-skeleton.component';

@Component({
  selector: 'rrp-user-accounts',
  templateUrl: './user-accounts.component.html',
  styleUrls: ['./user-accounts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,

  imports: [
    InfiniteScrollComponent,
    AsyncPipe,
    UserAccountItemComponent,
    TranslateModule,
    UserAccountItemSkeletonComponent,
    OptionListComponent,
  ],
})
export class UserAccountsComponent {
  private readonly currentPage$ = new BehaviorSubject<number>(0);

  private readonly userAccountsService = inject(UserAccountsService);
  private readonly userInformationService = inject(UserInformationService);
  private readonly currentAccountId$ = this.userInformationService
    .getCurrentAccount()
    .pipe(map((account) => account?.id ?? null));

  private readonly data$ = this.currentPage$.pipe(
    switchMap((pageNumber) =>
      this.userAccountsService.loadAccounts(
        pageNumber,
        this.userInformationService.currentAccount?.id ?? null,
      ),
    ),
    tap((response) => (this.canLoadMore = response.canLoadMore)),
    tap(() => this.loading$.next(false)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public readonly accounts$ = this.data$.pipe(
    scan(
      (acc: IUserAccount[], response: IUserAccounts) => [
        ...acc,
        ...response.items,
      ],
      [],
    ),
  );
  public readonly loading$ = new BehaviorSubject(true);
  public readonly asAccount = (account: unknown): IUserAccount =>
    account as IUserAccount;

  public readonly accountsSummary$: Observable<IUserAccount> =
    this.userAccountsService.loadSummary();
  public canLoadMore = true;

  public onUserAccountClick(account: IUserAccount | null): void {
    this.userInformationService.setCurrentAccount(account);
  }

  public isAccountActive(accountId: string): Observable<boolean> {
    return this.currentAccountId$.pipe(
      map((currentAccountId) => currentAccountId === accountId),
    );
  }

  public isCurrentAccountEmpty(): Observable<boolean> {
    return this.currentAccountId$.pipe(
      startWith(null),
      map((currentAccountId) => !currentAccountId),
    );
  }

  public onScrolled(): void {
    if (this.canLoadMore) {
      this.currentPage$.next(this.currentPage$.value + 1);
    }
  }
}
